import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { reportsApi, exportApi } from '../api/client';
import { useAuthStore } from '../store/authStore';
import { Card, CardHeader, CardTitle, CardBody } from '../components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/Table';
import Button from '../components/ui/Button';
import Input, { Select, Label } from '../components/ui/Input';
import Badge from '../components/ui/Badge';
import Modal from '../components/ui/Modal';
import { formatDateShort, getToday } from '../lib/dateUtils';
import { getStatusColor, downloadBlob, cn } from '../lib/utils';
import {
  Search,
  FileSpreadsheet,
  FileText,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Info,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from 'lucide-react';

const DIVISIONS = ['南一段', '南二段', '南三段', '南四段', '南五段', '南六段'];

// Sortable column header component
function SortableHeader({ column, label, currentSort, currentOrder, onSort, className }) {
  const isActive = currentSort === column;

  return (
    <button
      onClick={() => onSort(column)}
      className={cn(
        'flex items-center gap-1 hover:text-tpc-blue transition-colors w-full',
        isActive && 'text-tpc-blue font-semibold',
        className
      )}
    >
      {label}
      {isActive ? (
        currentOrder === 'asc' ? (
          <ArrowUp className="w-4 h-4" />
        ) : (
          <ArrowDown className="w-4 h-4" />
        )
      ) : (
        <ArrowUpDown className="w-3 h-3 opacity-40" />
      )}
    </button>
  );
}

export default function ReportsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasFullAccess } = useAuthStore();
  const locale = i18n.language;

  // Filters
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    division: '',
    status: '',
    keyword: '',
    page: 1,
    limit: 20,
    sortBy: 'reportDate',
    sortOrder: 'desc',
  });

  // Delete modal
  const [deleteModal, setDeleteModal] = useState({ open: false, report: null });
  const [deleteError, setDeleteError] = useState(null);

  // Without a date filter the server shows only today and later (預報工項)
  const showingUpcomingOnly = !filters.dateFrom && !filters.dateTo;

  // Fetch reports
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['reports', filters],
    queryFn: () => reportsApi.getAll(filters),
    select: (res) => res.data,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => reportsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['reports']);
      setDeleteModal({ open: false, report: null });
      setDeleteError(null);
    },
    onError: (error) => {
      setDeleteError(error.response?.data?.error || t('errors.generic', '操作失敗'));
    },
  });

  // Handle filter change - reset page to 1 only for non-page filters
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      ...(key !== 'page' && { page: 1 }), // Only reset page when changing other filters
    }));
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  // Handle sort
  const handleSort = (column) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: column,
      sortOrder: prev.sortBy === column && prev.sortOrder === 'desc' ? 'asc' : 'desc',
      page: 1,
    }));
  };

  const handleExport = async (format) => {
    try {
      const response = await exportApi.exportReports(filters, format);
      const filename = `TPC_Report_${new Date().toISOString().slice(0, 10)}.${format === 'word' ? 'docx' : 'xlsx'}`;
      downloadBlob(response.data, filename);
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const closeDeleteModal = () => {
    setDeleteModal({ open: false, report: null });
    setDeleteError(null);
  };

  // Calculate current page totals
  const currentPageData = data?.data || [];
  const pageWorkerTotal = currentPageData.reduce((sum, r) => sum + (r.totalWorkers || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">{t('reports.title')}</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => handleExport('excel')}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            {t('reports.exportExcel')}
          </Button>
          <Button variant="outline" onClick={() => handleExport('word')}>
            <FileText className="w-4 h-4 mr-2" />
            {t('reports.exportWord')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardBody>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <Label>{t('reports.dateRange')}</Label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              />
            </div>
            <div>
              <Label>&nbsp;</Label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              />
            </div>
            {hasFullAccess() && (
              <div>
                <Label>{t('reports.division')}</Label>
                <Select
                  value={filters.division}
                  onChange={(e) => handleFilterChange('division', e.target.value)}
                >
                  <option value="">{t('common.all')}</option>
                  {DIVISIONS.map((div) => (
                    <option key={div} value={div}>{div}</option>
                  ))}
                </Select>
              </div>
            )}
            <div>
              <Label>{t('common.status')}</Label>
              <Select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">{t('common.all')}</option>
                <option value="normal">{t('status.normal')}</option>
                <option value="overtime">{t('status.overtime')}</option>
                <option value="holiday">{t('status.holiday')}</option>
                <option value="holiday_overtime">{t('status.holiday_overtime')}</option>
                <option value="no_work">{t('status.no_work', '未出工')}</option>
              </Select>
            </div>
            <div>
              <Label>{t('reports.keyword')}</Label>
              <div className="relative">
                <Input
                  type="text"
                  value={filters.keyword}
                  onChange={(e) => handleFilterChange('keyword', e.target.value)}
                  placeholder={t('common.search')}
                  className="pl-9"
                />
                <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mt-4">
            <p className="flex items-center gap-1 text-sm text-gray-500">
              <Info className="w-4 h-4 flex-shrink-0" />
              {showingUpcomingOnly
                ? t('reports.upcomingOnlyHint', '目前顯示今日及之後的工項（預報工項）；查詢過期工項請設定日期範圍')
                : t('reports.dateFilteredHint', '依日期範圍顯示，清除日期即回到今日及之後')}
            </p>
            <Button variant="ghost" size="sm" onClick={() => refetch()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              {t('common.search')}
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Reports Table */}
      <Card>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-tpc-blue"></div>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <SortableHeader
                        column="reportDate"
                        label={t('reports.reportDate')}
                        currentSort={filters.sortBy}
                        currentOrder={filters.sortOrder}
                        onSort={handleSort}
                      />
                    </TableHead>
                    <TableHead>
                      <SortableHeader
                        column="division"
                        label={t('reports.division')}
                        currentSort={filters.sortBy}
                        currentOrder={filters.sortOrder}
                        onSort={handleSort}
                      />
                    </TableHead>
                    <TableHead>{t('reports.projectItemNo')}</TableHead>
                    <TableHead>{t('reports.projectName')}</TableHead>
                    <TableHead>{t('workItems.description')}</TableHead>
                    <TableHead className="text-right">{t('reports.workerCount')}</TableHead>
                    <TableHead>{t('workItems.entryTime')}</TableHead>
                    <TableHead>{t('workItems.exitTime')}</TableHead>
                    <TableHead>
                      <SortableHeader
                        column="status"
                        label={t('common.status')}
                        currentSort={filters.sortBy}
                        currentOrder={filters.sortOrder}
                        onSort={handleSort}
                      />
                    </TableHead>
                    <TableHead className="text-right">{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentPageData.length > 0 ? (
                    <>
                      {currentPageData.map((report) => (
                        <TableRow key={report.id}>
                          <TableCell>{formatDateShort(report.reportDate, locale)}</TableCell>
                          <TableCell>{report.division}</TableCell>
                          <TableCell className="text-center font-mono">
                            {report.projectItemNo || '-'}
                          </TableCell>
                          <TableCell className="max-w-[150px] truncate" title={report.projectName}>
                            {report.projectName}
                          </TableCell>
                          <TableCell className="max-w-[180px] truncate" title={report.workItemDescription}>
                            {report.workItemDescription || '-'}
                            {report.workItemCount > 1 && (
                              <span className="ml-1 text-xs text-gray-400">(+{report.workItemCount - 1})</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">{report.totalWorkers || 0}</TableCell>
                          <TableCell className="text-center">
                            {report.entryTime || '-'}
                          </TableCell>
                          <TableCell className="text-center">
                            {report.exitTime || '-'}
                          </TableCell>
                          <TableCell>
                            <Badge className={getStatusColor(report.status)}>
                              {t(`status.${report.status}`)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex justify-end gap-1">
                              {report.canEdit && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => navigate(`/reports/${report.id}/edit`)}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                              )}
                              {report.canDelete ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setDeleteModal({ open: true, report })}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              ) : report.canEdit && report.isExpired && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  disabled
                                  title={t('reports.expiredNoDelete', '過期通報不可刪除（保留供日後查核）')}
                                >
                                  <Trash2 className="w-4 h-4 text-gray-300" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Page Totals Row */}
                      <TableRow className="bg-gray-50 font-medium">
                        <TableCell colSpan={5} className="text-right">
                          {t('reports.pageTotal', '本頁合計')}:
                        </TableCell>
                        <TableCell className="text-right text-tpc-blue">{pageWorkerTotal}</TableCell>
                        <TableCell colSpan={4}></TableCell>
                      </TableRow>
                    </>
                  ) : (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center text-gray-500 py-12">
                        {t('common.noData')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {/* Pagination */}
              {data?.pagination && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                  <div className="text-gray-500">
                    {t('common.page')} {data.pagination.page} {t('common.of')} {data.pagination.totalPages}
                    {' '}({data.pagination.total} {t('common.items')})
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={data.pagination.page <= 1}
                      onClick={() => handlePageChange(data.pagination.page - 1)}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      {t('common.previous')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={data.pagination.page >= data.pagination.totalPages}
                      onClick={() => handlePageChange(data.pagination.page + 1)}
                    >
                      {t('common.next')}
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.open}
        onClose={closeDeleteModal}
        title={t('common.confirm')}
        size="sm"
      >
        <div className="space-y-4">
          <p>{t('reports.deleteConfirm')}</p>
          <p className="text-gray-500">
            {deleteModal.report?.projectName}
          </p>
          {deleteError && (
            <p className="text-sm text-red-600">{deleteError}</p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={closeDeleteModal}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              isLoading={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate(deleteModal.report?.id)}
            >
              {t('common.delete')}
            </Button>
          </div>
        </div>
      </Modal>

    </div>
  );
}
